/**
 * Order status types (matches backend OrderStatus enum)
 */
export enum OrderStatus {
  NEW = 'new',
  PENDING = 'pending',
  CANCELED = 'canceled',
  EXPIRED = 'expired',
  PAID = 'paid',
  FAILED = 'failed',
  COMPLETED = 'completed',
  REFUNDED = 'refunded',
}

/**
 * Gift card redeem details (matches backend RedeemDetails)
 */
export interface RedeemDetails {
  link?: string;
  fileUrl?: string;
  code?: string;
  secretCode?: string;
  securityCode?: string;
  serialNumber?: string;
  linkChallengeCode?: string;
  pin?: string;
}

/**
 * One card of an order, as the account pages get it (backend UserOrderLineDto)
 */
export interface OrderLine {
  productId: string;
  // Cards the line stands for: one, except on a few lines of 2025.
  quantity: number;
  faceValue: number;
  country: string;
  // The line totals are in this currency, not in the order currency.
  currency: string;
  total: number; // after discount
  totalNoDiscount: number;
  discountPct: number;
  // Present once every card of the order has its code, and only for the buyer.
  redeemDetails?: RedeemDetails;
}

/**
 * The payment of an order (backend UserOrderPaymentDto)
 */
export interface OrderPayment {
  provider?: string;
  // Provider checkout URL - present only while the order is still payable
  // (NEW/PENDING crypto orders). Lets the user re-open the payment page.
  url?: string;
}

/**
 * An order of the signed-in customer (backend UserOrderDto)
 */
export interface Order {
  _id: string;

  // Amounts
  total: number;
  totalNoDiscount?: number;
  currency: string;

  // Customer details
  email: string;
  giftEmail?: string;
  giftMessage?: string;

  status: OrderStatus;

  lines: OrderLine[];
  payment: OrderPayment;

  // Balance applied at checkout. 0 when no balance was used.
  // balanceAppliedCents is EUR cents (authoritative). The other two carry
  // the order-currency equivalent and the frozen EUR→order-ccy FX rate.
  balanceAppliedCents?: number;
  balanceAppliedInOrderCurrency?: number;
  balanceFxRate?: number;

  createdAt: Date | string;
}

/**
 * Post-purchase thumbs rating (matches backend OrderFeedbackRating)
 */
export enum OrderFeedbackRating {
  UP = 'UP',
  DOWN = 'DOWN',
}

/**
 * One card of an order on the public pages (backend OrderMetadataLineDto):
 * never a code.
 */
export type OrderMetadataLine = Omit<OrderLine, 'redeemDetails'>;

/**
 * What the success and bank pages get of an order (backend OrderMetadataDto)
 */
export interface OrderMetadata {
  id: string;
  status: OrderStatus;
  total: number;
  totalNoDiscount: number;
  currency: string;
  email: string;
  giftEmail?: string;
  lines: OrderMetadataLine[];
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Ad click-id query params recognised on a landing URL (mirrors the API's
 * AttributionClickId). Only the param name is recorded, never its value.
 */
export enum AttributionClickId {
  GCLID = 'gclid',
  GAD_SOURCE = 'gad_source',
  GBRAID = 'gbraid',
  WBRAID = 'wbraid',
  DCLID = 'dclid',
  FBCLID = 'fbclid',
  MSCLKID = 'msclkid',
  TTCLID = 'ttclid',
  TWCLID = 'twclid',
  LI_FAT_ID = 'li_fat_id',
  RDT_CID = 'rdt_cid',
  SCCID = 'sccid',
  EPIK = 'epik',
}

/**
 * One storefront landing (src/lib/attribution.ts). Sent with the order and
 * classified server-side; never forwarded to a third party.
 */
export interface OrderAttributionTouch {
  referrerDomain?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  clickId?: AttributionClickId;
  // The landing URL carried a ?ref=CODE friend-referral link (flag only).
  referralLink?: boolean;
  landingPath: string;
  capturedAt: number;
}

export interface OrderAttributionInput {
  // First landing ever seen in this browser (may be direct).
  first?: OrderAttributionTouch;
  // Last landing with an external referrer, campaign params or a click id.
  last?: OrderAttributionTouch;
}

/**
 * Request to create a new order (new structure based on BuyGiftCardDto)
 */
export interface CreateOrderRequest {
  email: string;
  giftEmail?: string;
  giftMessage?: string;
  currency: string;
  cartId: string;
  subscribeToNewsletter?: boolean;
  paymentMethod?: 'crypto' | 'bank_transfer';
  // Which crypto tile was picked (crypto only). The server maps it to a
  // provider; missing means the classic tile.
  payOption?: CryptoPayOption;
  couponCode?: string;

  gaClientId?: string;
  gaSessionId?: string;
  gclid?: string;
  fbclid?: string;
  posthogDistinctId?: string;
  // Landing attribution from src/lib/attribution.ts; classified server-side.
  attribution?: OrderAttributionInput;

  // Meta Pixel browser cookies - forwarded to Meta Conversions API on the server
  fbp?: string;
  fbc?: string;
  // Meta Pixel browser eventId - when present, the server fires CAPI
  // InitiateCheckout with the same id so it dedupes against the browser fbq.
  metaEventId?: string;
  // Whether the user granted marketing-category consent at checkout time
  marketingConsent?: boolean;
  // Express request to begin performance immediately, which forfeits the
  // statutory 14-day withdrawal right. Recorded on the order as proof of
  // consent - see Terms and Conditions, IV. Right of Withdrawal.
  withdrawalConsent?: boolean;
  // Optional: spend stored account balance (EUR cents). Server clamps and re-validates.
  balanceApplyCents?: number;
  // Saved payer bank account funding a SEPA transfer. Required by the server
  // whenever a bank_transfer order leaves an external residual.
  payerBankAccountId?: string;
  // Cloudflare Turnstile token. Required by the API when TURNSTILE_SECRET_KEY
  // is configured server-side; ignored otherwise.
  turnstileToken?: string;
}

// The two crypto checkout tiles; the tile -> provider mapping lives
// server-side (a constant of the API).
export type CryptoPayOption = 'classic' | 'stablecoin';

// Every payment tile of the cart. A product can block some of them
// (Card.blockedPaymentTiles); the cart never learns the provider behind.
export type PaymentTile = CryptoPayOption | 'bank_transfer';

export const PAYMENT_TILES: PaymentTile[] = ['classic', 'stablecoin', 'bank_transfer'];

// Tiles an admin switched off (GET /payments/tile-switches): shown grayed out as
// currently unavailable, or not shown at all. A tile in neither list is on.
export interface PaymentTileSwitches {
  unavailable: PaymentTile[];
  hidden: PaymentTile[];
}

/**
 * Response after order creation
 */
export type AssignedPaymentProvider = 'coingate' | 'triplea' | 'bank_transfer' | 'internal_balance';

export interface CreateOrderResponse {
  orderId: string;
  redirectUrl?: string;
  // The provider the SERVER actually assigned. Takes precedence over the
  // paymentMethod the FE originally sent - a bank-transfer request that
  // ended up 100% covered by balance comes back as 'internal_balance'.
  paymentProvider?: AssignedPaymentProvider;
}
